import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  BluetoothPrinter,
  WriteCharacteristic,
  scanForPrinters,
  connectToPrinter,
  disconnectFromPrinter,
  findWriteCharacteristic,
  isDeviceConnected,
  isWebBluetoothSupported,
} from '../hooks/use-web-bluetooth';

interface PrinterContextType {
  selectedPrinter: BluetoothPrinter | null;
  setSelectedPrinter: (printer: BluetoothPrinter | null) => Promise<void>;
  connectToSelectedPrinter: () => Promise<boolean>;
  getConnectedCharacteristic: () => Promise<WriteCharacteristic | null>;
  disconnectPrinter: () => void;
  scanForNewPrinter: (acceptAllDevices?: boolean) => Promise<void>;
  isConnecting: boolean;
  isConnected: boolean;
  writeCharacteristic: WriteCharacteristic | null;
  printerLabels: Map<string, string>;
  refreshPrinterLabel: (printerId: string) => Promise<void>;
  isSupported: boolean;
}

const PrinterContext = createContext<PrinterContextType | undefined>(undefined);

const SELECTED_PRINTER_KEY = 'pt-web-selected-printer';
const PRINTER_LABELS_KEY = 'pt-web-printer-labels';

interface PrinterProviderProps {
  children: ReactNode;
}

export const PrinterProvider: React.FC<PrinterProviderProps> = ({ children }) => {
  const [selectedPrinter, setSelectedPrinterState] = useState<BluetoothPrinter | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [writeCharacteristic, setWriteCharacteristic] = useState<WriteCharacteristic | null>(null);
  const [printerLabels, setPrinterLabels] = useState<Map<string, string>>(new Map());
  const [isSupported] = useState(isWebBluetoothSupported());

  // Load persisted printer labels from localStorage
  useEffect(() => {
    const storedLabelsJson = localStorage.getItem(PRINTER_LABELS_KEY);
    if (storedLabelsJson) {
      try {
        const obj = JSON.parse(storedLabelsJson) as Record<string, string>;
        setPrinterLabels(new Map(Object.entries(obj)));
      } catch (err) {
        console.warn('Failed to parse stored printer labels', err);
      }
    }
  }, []);

  // Persist labels whenever they change
  useEffect(() => {
    const obj = Object.fromEntries(printerLabels.entries());
    localStorage.setItem(PRINTER_LABELS_KEY, JSON.stringify(obj));
  }, [printerLabels]);

  // Handle device disconnection events
  useEffect(() => {
    if (!selectedPrinter?.device) return;

    const handleDisconnect = () => {
      console.log('Printer disconnected');
      setIsConnected(false);
      setWriteCharacteristic(null);
    };

    selectedPrinter.device.addEventListener('gattserverdisconnected', handleDisconnect);

    return () => {
      selectedPrinter.device.removeEventListener('gattserverdisconnected', handleDisconnect);
    };
  }, [selectedPrinter]);

  const connectToDevice = useCallback(async (printer: BluetoothPrinter): Promise<boolean> => {
    setIsConnecting(true);
    console.log('[PrinterContext] Starting connection to:', printer.name);
    
    try {
      // Check if already connected
      if (isDeviceConnected(printer.device)) {
        console.log('[PrinterContext] Printer already connected, finding write characteristic');
        setIsConnected(true);
        
        // Always find write characteristic for this device (even if we have one from another device)
        if (printer.device.gatt) {
          console.log('[PrinterContext] Finding write characteristic for already connected device');
          const char = await findWriteCharacteristic(printer.device.gatt);
          if (char) {
            setWriteCharacteristic(char);
            console.log('[PrinterContext] Write characteristic found for', printer.name);
          } else {
            console.warn('[PrinterContext] No write characteristic found on connected device');
          }
        }
        
        setIsConnecting(false);
        return true;
      }

      // Connect to the device
      console.log('[PrinterContext] Connecting to GATT server...');
      const server = await connectToPrinter(printer.device);
      if (!server) {
        console.error('[PrinterContext] Failed to connect to GATT server');
        setIsConnecting(false);
        return false;
      }

      console.log('[PrinterContext] Connected to GATT server, finding writable characteristic...');
      
      // Find writable characteristic
      const char = await findWriteCharacteristic(server);
      if (!char) {
        console.error('[PrinterContext] No writable characteristic found, disconnecting');
        disconnectFromPrinter(printer.device);
        setIsConnecting(false);
        return false;
      }

      console.log('[PrinterContext] Successfully found write characteristic for', printer.name);
      setWriteCharacteristic(char);
      setIsConnected(true);
      setIsConnecting(false);
      return true;
    } catch (error) {
      console.error('[PrinterContext] Error connecting to printer:', error);
      setIsConnecting(false);
      setIsConnected(false);
      return false;
    }
  }, []);

  const setSelectedPrinter = useCallback(async (printer: BluetoothPrinter | null) => {
    // Always disconnect from the current printer first
    if (selectedPrinter) {
      console.log('[PrinterContext] Disconnecting from current printer:', selectedPrinter.name);
      disconnectFromPrinter(selectedPrinter.device);
      setIsConnected(false);
      setWriteCharacteristic(null);
    }

    if (printer) {
      console.log('[PrinterContext] Switching to new printer:', printer.name);
      setSelectedPrinterState(printer);
      localStorage.setItem(SELECTED_PRINTER_KEY, JSON.stringify({
        name: printer.name,
        id: printer.id,
      }));
      
      // Attempt to connect to the new printer
      await connectToDevice(printer);
    } else {
      // No new printer, just clear everything
      console.log('[PrinterContext] Clearing printer selection');
      setSelectedPrinterState(null);
      setIsConnected(false);
      setWriteCharacteristic(null);
      localStorage.removeItem(SELECTED_PRINTER_KEY);
    }
  }, [selectedPrinter, connectToDevice]);

  const connectToSelectedPrinter = useCallback(async (): Promise<boolean> => {
    if (!selectedPrinter) {
      console.warn('No printer selected');
      return false;
    }
    return await connectToDevice(selectedPrinter);
  }, [selectedPrinter, connectToDevice]);

  const disconnectPrinter = useCallback(() => {
    if (selectedPrinter) {
      disconnectFromPrinter(selectedPrinter.device);
      setIsConnected(false);
      setWriteCharacteristic(null);
    }
  }, [selectedPrinter]);

  // Get a fresh connected characteristic - ensures connection is alive and returns fresh reference
  const getConnectedCharacteristic = useCallback(async (): Promise<WriteCharacteristic | null> => {
    if (!selectedPrinter) {
      console.warn('[PrinterContext] No printer selected');
      return null;
    }

    try {
      // Always reconnect to get fresh references
      console.log('[PrinterContext] Getting fresh connection and characteristic...');
      const server = await connectToPrinter(selectedPrinter.device);
      if (!server) {
        console.error('[PrinterContext] Failed to connect');
        setIsConnected(false);
        return null;
      }

      const char = await findWriteCharacteristic(server);
      if (!char) {
        console.error('[PrinterContext] No writable characteristic found');
        return null;
      }

      console.log('[PrinterContext] Got fresh characteristic:', char.uuid);
      setWriteCharacteristic(char);
      setIsConnected(true);
      return char;
    } catch (error) {
      console.error('[PrinterContext] Error getting characteristic:', error);
      setIsConnected(false);
      return null;
    }
  }, [selectedPrinter]);

  const scanForNewPrinter = useCallback(async (acceptAllDevices = true) => {
    try {
      console.log('[PrinterContext] scanForNewPrinter, acceptAllDevices:', acceptAllDevices);
      const printer = await scanForPrinters(acceptAllDevices);
      if (printer) {
        await setSelectedPrinter(printer);
      }
    } catch (error) {
      console.error('Error scanning for printers:', error);
      throw error;
    }
  }, [setSelectedPrinter]);

  const refreshPrinterLabel = useCallback(async (printerId: string) => {
    // Check if we already have the label
    if (printerLabels.has(printerId)) {
      return;
    }

    try {
      // Fetch label from backend
      // Note: This would need to be implemented with actual API call
      // For now, we'll use the printer name as the label
      const label = printerId;
      setPrinterLabels(prev => {
        const updated = new Map(prev);
        updated.set(printerId, label);
        return updated;
      });
    } catch (error) {
      console.error('Error fetching printer label:', error);
    }
  }, [printerLabels]);

  const value: PrinterContextType = {
    selectedPrinter,
    setSelectedPrinter,
    connectToSelectedPrinter,
    getConnectedCharacteristic,
    disconnectPrinter,
    scanForNewPrinter,
    isConnecting,
    isConnected,
    writeCharacteristic,
    printerLabels,
    refreshPrinterLabel,
    isSupported,
  };

  return <PrinterContext.Provider value={value}>{children}</PrinterContext.Provider>;
};

export const usePrinter = (): PrinterContextType => {
  const context = useContext(PrinterContext);
  if (!context) {
    throw new Error('usePrinter must be used within a PrinterProvider');
  }
  return context;
};

